import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
    Button, Drawer, Form, Input, InputNumber, message, Popconfirm, Select, Space,
    Table, Tag, Tooltip, Typography, Avatar, Card, Switch, Spin
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { TablePaginationConfig } from 'antd/es/table'
import { useSearchParams } from 'react-router-dom'
import { useAppContext } from '@/components/AppContext'
import { TempleListApiData, TempleListBody, TempleListsResponse } from '@a4b/api/src/modules/Monetisation/temple/types'
import S3Upload, { AllowedFormats } from '@a4b/ui/src/modules/monetisation/molecules/S3Upload'
import { YODA_CDN_URL, YODA_PRESIGN_URL } from '@/utils/constants'
import MediaListFieldset from './MediaListFieldset'
import styled from 'styled-components'

type Row = {
    id: string
    temple_name: string
    is_deleted?: boolean
    email?: string
    contact?: string
    website?: string
    created_at?: string
    logo?: string
}

/** ----------------- helpers ----------------- */
const normFileList = (e: any) => Array.isArray(e) ? e : (e?.fileList ?? [])
const isUploading = (list: any[]) => list?.some((f) => f?.status && f.status !== 'done')

const fileItemToUrl = (f: any): string =>
    f?.url || f?.cdnUrl || f?.response?.cdnUrl || f?.response?.Location || f?.response?.data?.url || (typeof f === 'string' ? f : '')

const pickUrlFromFileList = (list?: any[]): string => {
    if (!Array.isArray(list) || list.length === 0) return ''
    const f = list.find(x => x?.status === 'done') ?? list[list.length - 1]
    return fileItemToUrl(f)
}

/** for preview: path → absolute CDN URL; absolute stays absolute */
const toAbsoluteCdnUrl = (maybePath: string): string => {
    if (!maybePath) return ''
    if (/^https?:\/\//i.test(maybePath)) return maybePath
    const base = (YODA_CDN_URL || '').replace(/\/+$/, '')
    const path = maybePath.startsWith('/') ? maybePath : `/${maybePath}`
    return `${base}${path}`
}

/** turn any absolute CDN URL into a path-only string (no scheme/host) */
const toCdnPathOnly = (url: string): string => {
    if (!url) return ''
    if (url.startsWith('/')) return url.split('?')[0]
    if (/^https?:\/\//i.test(url)) {
        try { return new URL(url).pathname } catch { /* ignore */ }
    }
    const stripped = url.replace(/^https?:\/\/[^/]+/i, '')
    return stripped.startsWith('/') ? stripped.split('?')[0] : `/${stripped.split('?')[0]}`
}

const extractExt = (url: string): string => {
    try {
        const noQuery = url.split('?')[0]
        const match = noQuery.match(/\.([a-zA-Z0-9]+)$/)
        return match ? match[1].toLowerCase() : ''
    } catch { return '' }
}
const inferTypeFromUrlSync = (url?: string): 'image' | 'video' | 'audio' => {
    const ext = extractExt(url || '')
    if (ext === 'mp4') return 'video'
    if (ext === 'mp3') return 'audio'
    return 'image'
}

const imageExts = [AllowedFormats.PNG, AllowedFormats.JPEG, AllowedFormats.JPG, AllowedFormats.WEBP, AllowedFormats.GIF]

/** ----------------- table row mapper ----------------- */
const mapDetailsToRow = (d: TempleListsResponse): Row => ({
    id: d.id || '',
    temple_name: d.temple_name,
    is_deleted: d.is_deleted,
    logo: (d as any).logo || '',
})

/** =======================================================================
 * ManageTemple
 * ======================================================================= */
const ManageTemple: React.FC = () => {

    const { networkInstance, countryState, languageState } = useAppContext()
    const [form] = Form.useForm()

    // FULL-SCREEN SAVE LOADER (new)
    const [savingOverlay, setSavingOverlay] = useState(false)

    const timeOptions = useMemo(() => {
        const out: string[] = []
        for (let h = 0; h < 24; h++) {
            for (let m = 0; m < 60; m += 30) {
                const hr12 = ((h % 12) || 12).toString().padStart(2, '0')
                const mm = m === 0 ? '00' : '30'
                const ampm = h < 12 ? 'AM' : 'PM'
                out.push(`${hr12}:${mm} ${ampm}`)
            }
        }
        return out
    }, [])

    // table state
    const [pageRows, setPageRows] = useState<Row[]>([])
    const [total, setTotal] = useState<number>(0)
    const [loading, setLoading] = useState(false) // list/edit loading (not the full-screen save)
    const [pagination, setPagination] = useState<TablePaginationConfig>({ current: 1, pageSize: 10 })

    // filters
    const [title, setTitle] = useState('')
    const [id, setId] = useState('')
    const [searchParams] = useSearchParams()
    const searchKey = `${searchParams.get('id') ?? ''}|${searchParams.get('title') ?? ''}`

    // drawer/form
    const [panelOpen, setPanelOpen] = useState(false)
    const [editingId, setEditingId] = useState<string | null>(null)

    // trustees select
    const [trusteeOptions, setTrusteeOptions] = useState<
        Array<{ value: string; name: string; img?: string }>
    >([])

    // mounted guard
    const mountedRef = useRef(true)
    useEffect(() => {
        mountedRef.current = true
        return () => { mountedRef.current = false }
    }, [])

    /** ---------------- fetch list ---------------- */
    const getTempleList = useCallback(async () => {
        setLoading(true)
        try {
            const qpTitle = (title?.trim() || searchParams.get('title') || undefined) ?? undefined
            const qpId = (id?.trim() || searchParams.get('id') || undefined) ?? undefined

            const pageSize = Number(pagination.pageSize || 10)
            const current = Number(pagination.current || 1)
            const limit = pageSize
            const offset = (current - 1) * limit

            const res = await networkInstance.clientWithHeaders.shrineApi.getTemples(
                { limit, offset }, qpId, qpTitle, countryState?.countryCode, languageState?.monetisationLanguage
            ) as { data?: TempleListBody } | TempleListBody | any

            const body: TempleListBody | undefined =
                res?.data?.data && res.data ? res.data
                    : (res?.data && res.data.data) ? res
                        : undefined

            const api: TempleListApiData | undefined = body?.data
            const list: TempleListsResponse[] = api?.temples ?? []
            const mapped = list.map(mapDetailsToRow)

            if (!mountedRef.current) return
            setPageRows(mapped)
            setTotal(typeof api?.count === 'number' ? api.count : offset + mapped.length)
        } catch {
            if (!mountedRef.current) return
            message.error({ content: 'Error while fetching temple list', duration: 3 })
        } finally {
            if (mountedRef.current) setLoading(false)
        }
    }, [networkInstance, pagination.current, pagination.pageSize, title, id, searchKey, countryState?.countryCode, languageState?.monetisationLanguage, searchParams])

    useEffect(() => { getTempleList() }, [getTempleList, countryState?.countryCode, languageState?.monetisationLanguage])

    /** ---------------- trustees for select ---------------- */
    const loadTrusteeOptions = useCallback(async () => {
        try {
            const res = await networkInstance.clientWithHeaders.shrineApi.getTrustees(
                { limit: 100, offset: 0 },
                undefined,
                undefined,
                countryState?.countryCode,
                languageState?.monetisationLanguage
            )

            const api = (res?.data?.data ?? res?.data) as
                | { trustees?: any[]; count?: number }
                | undefined

            const list = Array.isArray(api?.trustees) ? api!.trustees : []

            setTrusteeOptions(
                list.map((t: any) => ({
                    value: t.id,
                    name: t.name || t.id,
                    img: t?.media?.url || '',
                }))
            )
        } catch (e: any) {
            message.error(e?.response?.data?.message || 'Failed to load trustees')
        }
    }, [networkInstance])


    // add these
    const resetToFirstPage = () =>
        setPagination(prev => (prev.current === 1 ? prev : { ...prev, current: 1 }))

    const onTitleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setTitle(e.target.value)
        resetToFirstPage()
    }

    const onIdFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setId(e.target.value)
        resetToFirstPage()
    }

    /** ---------------- CRUD ---------------- */
    const openingRef = useRef(false)
    const openCreate = async () => {
        if (openingRef.current) return
        openingRef.current = true
        setEditingId(null)
        form.resetFields()
        await loadTrusteeOptions()
        setPanelOpen(true)
        form.setFieldsValue({
            is_autofill: false,
            logo: { url: [] },
            metadata: { air: '', train: '', road: '' },
            about_us_media: [], banners: [], darshans: [], gallery: [],
        })
        openingRef.current = false
    }

    const openEdit = async (r: Row) => {
        setEditingId(r.id)
        setLoading(true)
        await loadTrusteeOptions()
        try {
            const mp = await networkInstance.clientWithHeaders.shrineApi.getTemple(r.id, countryState?.countryCode, languageState?.monetisationLanguage)
            const d = mp?.data?.data || {}
            const md = d.metadata || {}

            const toFormList = (arr?: any[]) =>
                Array.isArray(arr)
                    ? [...arr].sort((a, b) => (a?.position || 0) - (b?.position || 0)).map(m => ({
                        type: m?.type || inferTypeFromUrlSync(m?.url || ''),
                        url: [toAbsoluteCdnUrl(m?.url || '')],
                    }))
                    : []

            const trusteeIds =
                Array.isArray(d.trustees)
                    ? d.trustees.map((x: any) => typeof x === 'string' ? x : x?.id).filter(Boolean)
                    : []

            const metaFields: any = {
                air: typeof md.air === 'string' ? md.air : '',
                train: typeof md.train === 'string' ? md.train : '',
                road: typeof md.road === 'string' ? md.road : '',
            }
            if (md.open_time) metaFields.open_time = md.open_time
            if (md.close_time) metaFields.close_time = md.close_time

            form.resetFields()
            form.setFieldsValue({
                temple_name: d.temple_name || r.temple_name,
                description: d.description || '',
                website: d.website || r.website || '',
                email: d.email || r.email || '',
                phoneNumber: d.phone || r.contact || '',
                address: d.address || '',
                lat: typeof d.lat === 'number' ? d.lat : 0,
                lng: typeof d.lng === 'number' ? d.lng : 0,
                metadata: metaFields,
                about_us: d.about_us || '',
                trustees: trusteeIds,
                logo: { url: d.logo ? [toAbsoluteCdnUrl(d.logo)] : [] },
                about_us_media: toFormList(d.about_us_media),
                banners: toFormList(d.banners),
                darshans: toFormList(d.darshans),
                gallery: toFormList(d.gallery),
            })
        } catch (e: any) {
            message.error(e?.response?.data?.message || 'Failed to fetch temple')
        } finally {
            setPanelOpen(true)
            setLoading(false)
        }
    }

    const toApiPayload = (values: any) => {
        const withPos = (arr?: any[]) => (arr || [])
            .map((row: any, i: number) => {
                const fullUrl = pickUrlFromFileList(row?.url || [])
                const path = toCdnPathOnly(fullUrl)
                if (!path) return null
                return {
                    type: row?.type || inferTypeFromUrlSync(path),
                    url: path,
                    position: i + 1,
                }
            })
            .filter(Boolean)

        // logo object { type, url } when present
        const logoList: any[] = values?.logo?.url || []
        const logoFullUrl = pickUrlFromFileList(logoList)
        const logoPath = toCdnPathOnly(logoFullUrl)
        const logoType = inferTypeFromUrlSync(logoPath)
        const logo = logoPath ? { type: logoType, url: logoPath } : undefined

        // normalize numbers: always include lat/lng; null when empty
        const normalizeNum = (v: any) => {
            if (v === '' || v === undefined) return null
            if (v === null) return null
            const n = Number(v)
            return Number.isNaN(n) ? null : n
        }
        // const lat = normalizeNum(values?.lat)
        // const lng = normalizeNum(values?.lng)
        const lat = values?.lat ?? 0
        const lng = values?.lng ?? 0

        // metadata: always include open/close; empty string when not provided
        const mdIn = values?.metadata || {}
        const metadata: any = {
            open_time: typeof mdIn.open_time === 'string' ? mdIn.open_time : '',
            close_time: typeof mdIn.close_time === 'string' ? mdIn.close_time : '',
            air: typeof mdIn.air === 'string' ? mdIn.air : '',
            train: typeof mdIn.train === 'string' ? mdIn.train : '',
            road: typeof mdIn.road === 'string' ? mdIn.road : '',
        }

        const out: any = {
            temple_name: (values.temple_name || '').trim(),
            description: values.description || '',
            address: values.address || '',
            email: values.email || '',
            phone: values.phoneNumber || '',
            website: values.website || '',
            about_us: values.about_us || '',
            trustees: Array.isArray(values.trustees) ? values.trustees : [],
            about_us_media: withPos(values.about_us_media),
            banners: withPos(values.banners),
            darshans: withPos(values.darshans),
            gallery: withPos(values.gallery),
            lat,
            lng,
            metadata,
        }

        if (logo) out.logo = logo
        return out
    }

    const onSave = async () => {
        // 1) Validate first (keep drawer open if invalid)
        try {
            await form.validateFields()
        } catch {
            return
        }

        // 2) Block if any uploads in progress
        const blocks: any[] = []
        const logoList: any[] = form.getFieldValue(['logo', 'url']) || []
        if (isUploading(logoList)) blocks.push('logo')
            ;['about_us_media', 'banners', 'darshans', 'gallery'].forEach((key) => {
                const rows: any[] = form.getFieldValue([key]) || []
                rows.forEach((row) => {
                    const list: any[] = row?.url || []
                    if (isUploading(list)) blocks.push(key)
                })
            })
        if (blocks.length) {
            message.warning('Please wait for all media uploads to finish')
            return
        }

        // 3) Build payload
        const values = form.getFieldsValue(true)
        const payload = toApiPayload(values)

        // 4) Close drawer instantly and show FULL-SCREEN overlay
        setPanelOpen(false)
        setSavingOverlay(true)

        try {
            if (editingId) {
                await networkInstance.clientWithHeaders.shrineApi.updateTemple(
                    editingId, payload as any, countryState?.countryCode, languageState?.monetisationLanguage
                )
                message.success('Temple updated')
            } else {
                const isAutofill = !!form.getFieldValue('is_autofill')
                await networkInstance.clientWithHeaders.shrineApi.createTemple(
                    { ...payload, is_autofill: isAutofill } as any, countryState?.countryCode, languageState?.monetisationLanguage
                )
                message.success('Temple created')
            }

            // 5) Refresh list
            await getTempleList()
        } catch (e: any) {
            // show error toast
            message.error(e?.response?.data?.message || 'Failed to save temple')
        } finally {
            // 6) Hide overlay in all cases
            setSavingOverlay(false)
        }
    }

    const onDelete = async (id: string) => {
        try {
            setLoading(true)
            await networkInstance.clientWithHeaders.shrineApi.deleteTemple(id, countryState?.countryCode, languageState?.monetisationLanguage)
            message.success('Temple deleted')
            await getTempleList()
        } catch (e: any) {
            message.error(e?.response?.data?.message || 'Failed to delete temple')
        } finally {
            setLoading(false)
        }
    }

    /** ---------------- table ---------------- */
    const TitleFilter = (
        <Input
            placeholder="Search Title"
            value={title}
            onChange={onTitleFilterChange}
            allowClear
        />
    )

    const IdFilter = (
        <Input
            placeholder="Search ID"
            value={id}
            onChange={onIdFilterChange}
            allowClear
        />
    )

    const columns: ColumnsType<Row> = useMemo(() => ([
        {
            title: 'Logo',
            dataIndex: 'logo',
            key: 'logo',
            width: 64,
            render: (_: any, r) => (
                <Avatar
                    shape="square"
                    size={40}
                    src={toAbsoluteCdnUrl(r.logo || '') || undefined}
                    alt={r.temple_name}
                    style={{ background: '#f0f0f0' }}
                >
                    {r.temple_name?.[0]?.toUpperCase()}
                </Avatar>
            ),
        },
        {
            title: (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span>Title</span>
                    {/* <div>{TitleFilter}</div> */}
                </div>
            ),
            dataIndex: 'temple_name',
            key: 'temple_name',
            width: '25%',
            render: (v: string) => <span style={{ fontWeight: 500 }}>{v}</span>,
        },
        {
            title: (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span>Temple Id</span><div>{IdFilter}</div>
                </div>
            ),
            dataIndex: 'id',
            key: 'id',
            width: '22%',
            ellipsis: true,
            render: (val: string) => <Tooltip placement="topLeft" title={val}>{val}</Tooltip>,
        },
        {
            title: 'Status',
            key: 'status',
            width: 100,
            render: (_: any, r) => (r.is_deleted ? <Tag>Archived</Tag> : <Tag color="green">Active</Tag>),
        },
        {
            title: 'Action',
            key: 'action',
            width: 220,
            render: (_: any, r) => (
                <Space size="middle">
                    <Button type="link" onClick={() => openEdit(r)}>Edit</Button>
                    <Popconfirm title="Delete temple?" onConfirm={() => onDelete(r.id)}>
                        <Button type="link" danger>Delete</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ]), [TitleFilter, IdFilter])

    const onPageChange: NonNullable<TablePaginationConfig['onChange']> = (page, pageSize) =>
        setPagination(prev => ({ ...prev, current: page, pageSize: pageSize ?? prev.pageSize ?? 10 }))

    const Container = styled(Card)`
      display: flex;
      flex-direction: column;
      .ant-card-body {
        flex-grow: 1;
      }
    `

    const FullScreenOverlay = styled.div`
      position: fixed;
      inset: 0;
      background: rgba(255,255,255,0.75);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000; /* above drawer & table */
      backdrop-filter: blur(1px);
    `

    return (
        <>
            {/* Full-Screen loader overlay */}
            {savingOverlay && (
                <FullScreenOverlay aria-live="polite" aria-busy="true" role="alert" data-testid="saving-overlay">
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                        <Spin tip="Saving… Please wait" size="large" />
                        <Typography.Text type="secondary" style={{ textAlign: 'center' }}>
                            This might take a moment if AI Autofill is on.
                        </Typography.Text>
                    </div>
                </FullScreenOverlay>
            )}

            <Container title='Manage Temples Profile'
                style={{ width: '100%' }}
                extra={
                    <Button type="primary" onClick={openCreate}>Add new Temple</Button>
                }
            >

                <Table<Row>
                    rowKey="id"
                    loading={loading}
                    columns={columns}
                    dataSource={pageRows}
                    pagination={{
                        current: pagination.current!,
                        pageSize: pagination.pageSize!,
                        total,
                        onChange: onPageChange,
                        showSizeChanger: false,
                    }}
                />

                <Drawer
                    title={editingId ? 'Edit Temple' : 'Create Temple'}
                    width={820}
                    visible={panelOpen}          // If on AntD v5, replace with: open={panelOpen}
                    onClose={() => setPanelOpen(false)}
                    destroyOnClose={false}
                    forceRender
                    footer={
                        <div style={{ textAlign: 'right' }}>
                            <Space>
                                <Button onClick={() => setPanelOpen(false)}>Cancel</Button>
                                <Button type="primary" onClick={onSave} loading={savingOverlay} disabled={savingOverlay}>
                                    {editingId ? 'Save Changes' : 'Create Temple'}
                                </Button>
                            </Space>
                        </div>
                    }
                >
                    <Form
                        form={form}
                        layout="vertical"
                        name="temple-form"
                        labelCol={{ span: 24 }}
                        wrapperCol={{ span: 24 }}
                        initialValues={{
                            logo: { url: [] },
                            about_us_media: [], banners: [], darshans: [], gallery: []
                        }}
                    >
                        {!editingId && (
                            <Form.Item
                                label="isAutofill"
                                tooltip="Enable this to let AI create the content after you enter just the Temple Name."
                                extra="Turn this on and provide only the temple name and hit the create button — the rest will be managed by AI."
                            >
                                <Space align="center">
                                    <Form.Item name="is_autofill" valuePropName="checked" noStyle>
                                        <Switch />
                                    </Form.Item>
                                    <Typography.Text type="secondary">Enable AI to fill details</Typography.Text>
                                </Space>
                            </Form.Item>
                        )}

                        {/* BASIC */}
                        <Form.Item label="Temple Name" name="temple_name" rules={[{ required: true, message: 'Please input temple name' }]}>
                            <Input placeholder="Enter temple name" />
                        </Form.Item>

                        <Space size={12} style={{ width: '100%' }} align="start" wrap>
                            <Form.Item style={{ flex: 1 }} label="Website" name="website">
                                <Input placeholder="https://…" />
                            </Form.Item>
                            <Form.Item style={{ flex: 1 }} label="Email" name="email">
                                <Input placeholder="abc@xyz.com" />
                            </Form.Item>
                            <Form.Item style={{ flex: 1 }} label="Phone" name="phoneNumber">
                                <Input placeholder="+91…" />
                            </Form.Item>
                        </Space>

                        <Form.Item label="Address" name="address">
                            <Input placeholder="Temple address" />
                        </Form.Item>

                        {/* LOGO via S3Upload (single) */}
                        <Form.Item
                            label="Logo"
                            name={['logo', 'url']}
                            valuePropName="fileList"
                            getValueFromEvent={normFileList}
                            extra="Upload temple logo (image)"
                            rules={[{ min: 0, max: 1, validator: async () => undefined }]}
                        >
                            <S3Upload
                                cdnUrl={YODA_CDN_URL}
                                preSignUrl={YODA_PRESIGN_URL}
                                min={0}
                                max={1}
                                allowedFileExtension={imageExts}
                                skipTruncate={true}
                            />
                        </Form.Item>

                        <Form.Item label="Short Description" name="description">
                            <Input.TextArea rows={3} placeholder="Temple short description" />
                        </Form.Item>

                        {/* TIMINGS */}
                        <Space size={12} style={{ width: '100%' }} align="start" wrap>
                            <Form.Item style={{ flex: 1 }} label="Open Time" name={['metadata', 'open_time']}>
                                <Select
                                    showSearch
                                    placeholder="Select time"
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    allowClear
                                >
                                    {timeOptions.map(t => (
                                        <Select.Option key={t} value={t}>{t}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item style={{ flex: 1 }} label="Close Time" name={['metadata', 'close_time']}>
                                <Select
                                    showSearch
                                    placeholder="Select time"
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    allowClear
                                >
                                    {timeOptions.map(t => (
                                        <Select.Option key={t} value={t}>{t}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Space>

                        {/* TRAVEL INFO */}
                        <Form.Item label="How to reach — Air" name={['metadata', 'air']}>
                            <Input placeholder="Nearest airport…" />
                        </Form.Item>
                        <Form.Item label="How to reach — Train" name={['metadata', 'train']}>
                            <Input placeholder="Nearest railway station…" />
                        </Form.Item>
                        <Form.Item label="How to reach — Road" name={['metadata', 'road']}>
                            <Input placeholder="Road connectivity…" />
                        </Form.Item>

                        {/* GEO */}
                        <Space size={12} style={{ width: '100%' }} align="start" wrap>
                            <Form.Item style={{ flex: 1 }} label="Latitude" name="lat">
                                <InputNumber style={{ width: '100%' }} placeholder="e.g., 16.0726" />
                            </Form.Item>
                            <Form.Item style={{ flex: 1 }} label="Longitude" name="lng">
                                <InputNumber style={{ width: '100%' }} placeholder="e.g., 78.8685" />
                            </Form.Item>
                        </Space>

                        {/* ABOUT US + MEDIA */}
                        <Form.Item label="About Us" name="about_us">
                            <Input.TextArea rows={4} placeholder="Longer temple description" />
                        </Form.Item>
                        <MediaListFieldset legend="About Us Media" name="about_us_media" allowed={imageExts} />

                        {/* FEATURED MEDIA */}
                        <MediaListFieldset legend="Banners" name="banners" allowed={imageExts} />
                        <MediaListFieldset legend="Darshans" name="darshans" allowed={imageExts} />
                        <MediaListFieldset legend="Gallery" name="gallery" allowed={imageExts} />

                        {/* TRUSTEES SELECT */}
                        <Form.Item label="Trustees" name="trustees">
                            <Select
                                mode="multiple"
                                style={{ minWidth: 360 }}
                                placeholder="Select trustees"
                                showSearch
                                optionLabelProp="data-label"
                                filterOption={(input, option) =>
                                    String((option?.props as any)?.['data-name'] || '')
                                        .toLowerCase()
                                        .includes(input.toLowerCase())
                                }
                                allowClear
                            >
                                {trusteeOptions.map(o => (
                                    <Select.Option
                                        key={o.value}
                                        value={o.value}
                                        data-label={o.name}
                                        data-name={o.name}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <Avatar size={24} src={o.img || undefined}>
                                                {!o.img && (o.name?.[0] || '').toUpperCase()}
                                            </Avatar>
                                            <span>{o.name}</span>
                                        </div>
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Form>
                </Drawer>
            </Container>
        </>
    )
}

export default ManageTemple
